import type { SubsetFact } from '../../types/types.js';
import { stripTags } from '../../utils/sanitize.js';
import { getFactSourceText } from '../../utils/getFactSourceText.js';
import { capitalizeFirstLetter } from '../../utils/capitalizeFirstLetter.js';

export enum FactDownloadColumn {
  DateVerbatim = 'Date (verbatim)',
  DateStandardised = 'Date (standardised)',
  Description = 'Fact',
  Relevance = 'Relevance',
  SourceText = 'Source Text',
  SourceDocument = 'Source Document',
  Classification = 'Classification',
  Key = 'Key',
  Disputed = 'Disputed',
  Id = 'Id',
  Notes = 'Notes',
  NotesDate = 'Notes Date',
  NotesBy = 'Notes By',
}

export const FactDownloadColumnOptions = Object.values(FactDownloadColumn);

// Excel cell character limit is 32,767 — 10,000 is safely under it (and anything longer is unreadable anyway).
const CELL_LENGTH_LIMIT = 10000;

export function prepareFactCSVRow(fact: SubsetFact, columns: FactDownloadColumn[]): string[] {
  let sourceText = getFactSourceText(fact);

  if (sourceText !== null && sourceText.length > CELL_LENGTH_LIMIT) {
    sourceText = `${sourceText.slice(0, CELL_LENGTH_LIMIT)}...`;
  }

  const firstComment = fact.comments.at(0);

  const columnMapping: Record<FactDownloadColumn, string | Date | boolean | null | undefined> = {
    [FactDownloadColumn.DateVerbatim]: fact.factDateText,
    [FactDownloadColumn.DateStandardised]: fact.factDate,
    [FactDownloadColumn.Description]: fact.description,
    [FactDownloadColumn.Relevance]:
      fact.relevance !== null ? capitalizeFirstLetter(fact.relevance) : null,
    [FactDownloadColumn.SourceText]: stripTags(sourceText),
    [FactDownloadColumn.SourceDocument]: fact.locations.at(0)?.doc.file.name,
    [FactDownloadColumn.Classification]: fact.classification,
    [FactDownloadColumn.Key]: fact.isKey,
    [FactDownloadColumn.Disputed]: fact.isDisputed,
    [FactDownloadColumn.Id]: fact.id,
    [FactDownloadColumn.Notes]: firstComment?.content,
    [FactDownloadColumn.NotesDate]: firstComment?.createdDate,
    [FactDownloadColumn.NotesBy]: firstComment?.createdBy.email,
  };

  return columns.map((column) => columnMapping[column]?.toString() ?? '');
}

export function extractUniqueDocumentsFromFacts(facts: SubsetFact[]): string[] {
  const uniqueDocIds = new Set<string>();
  for (const fact of facts) {
    for (const location of fact.locations) {
      if (location.docId !== '') uniqueDocIds.add(location.docId);
    }
  }
  return Array.from(uniqueDocIds).sort();
}
